<script lang="ts">
    import { mdiTagEditOutline } from "@mdi/js"
    import { overlay } from "../../../../../store"
    import Lasersheets from "../../widgets/lasersheet/Lasersheets.svelte"
    import Icon from "../../../../widgets/Icon.svelte"
    import { langField } from "../../../../../i18n"
    import { withImageCacheBuster } from "../../../../../utils"

    interface Props {
        image: ImageEntry
        product: Product
        shadow?: boolean
        showLasersheetActivation?: boolean
    }

    let { image, product, shadow = false, showLasersheetActivation }: Props = $props()
    if (!image) {
        image = {
            file: { src: "/media/kein-bild.jpg" },
        }
    }
    let shouldZoom = $state(false),
        imagePosition: HTMLButtonElement = $state(),
        coordX = $state(50),
        coordY = $state(50),
        lastTouchMoveEvent: number = 0

    function handleMousemove(event: MouseEvent) {
        if (!shouldZoom) return
        updatePosition(event.clientX, event.clientY)
    }

    function handleTouchMove(event: TouchEvent) {
        if (!shouldZoom) return
        event.preventDefault()
        const now = Date.now()
        if (now - lastTouchMoveEvent < 10) return
        lastTouchMoveEvent = now
        updatePosition(event.touches[0].clientX, event.touches[0].clientY)
    }

    function handleClick(event: MouseEvent | TouchEvent) {
        shouldZoom = !shouldZoom
        if (shouldZoom) {
            if (event instanceof MouseEvent) handleMousemove(event)
            else if (event instanceof TouchEvent) handleTouchMove(event)
        } else resetPosition()
    }

    function updatePosition(clientX: number, clientY: number) {
        const rect = imagePosition.getBoundingClientRect()
        // Mouse position as 0–1 ratio within the container
        const ratioX = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
        const ratioY = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height))
        // Map full mouse range to full image range so edges are reachable
        coordX = ratioX * 100
        coordY = ratioY * 100
    }

    function resetPosition() {
        coordX = 50
        coordY = 50
    }

    const hasLasersheets = $derived(!!(product.images?.length && product.lasersheet_configurator?.length))
</script>

{#if image?.file?.src}
    {#key image?.file?.src}
        <div class="imageholder primary-image" class:scenery-image={image?.sceneryImage}>
            <button
                class="image button transparent"
                aria-label="zoom in image"
                class:allowZoom={shouldZoom}
                onclick={handleClick}
                onmousemove={handleMousemove}
                ontouchmove={handleTouchMove}
                bind:this={imagePosition}
            >
                <div class="image-content" style="transform-origin: {coordX}% {coordY}%;">
                    <img
                        src={withImageCacheBuster(
                            image.file.src +
                                "?filter=1080" +
                                (shadow ? "-shadow" + (hasLasersheets ? "-fixed" : "") : "")
                        )}
                        alt={image?.label || langField(product.name)}
                        width="1080"
                        height="1080"
                    />
                    {#if hasLasersheets}
                        <Lasersheets {product} {image} />
                    {/if}
                </div>
            </button>
            {#if showLasersheetActivation}
                <button
                    class="button lasersheet-button"
                    aria-label="lasersheet activation"
                    onclick={() => {
                        $overlay = {
                            show: true,
                            type: "lasersheet",
                            properties: {
                                product,
                                setActiveImage: () => {},
                            },
                        }
                    }}
                >
                    <Icon path={mdiTagEditOutline} size="24px" />
                </button>
            {/if}
            {#if product.note_label}
                {#if product.note_text}
                    <button class="note-badge has-tooltip" aria-label={product.note_label}>
                        <span class="note-label">{product.note_label}</span>
                        <span class="note-tooltip">{product.note_text}</span>
                    </button>
                {:else}
                    <div class="note-badge">
                        <span class="note-label">{product.note_label}</span>
                    </div>
                {/if}
            {/if}
        </div>
    {/key}
{/if}

<style lang="less" global>
    @import "../../../../../css/variables.less";
    .imageholder.primary-image {
        margin: 0px auto;
        &.scenery-image {
            padding: 20px;
            background: var(--grey-100);
        }

        width: 100%;

        height: 705px;
        flex-shrink: 0;
        display: flex;
        justify-content: center;
        position: relative;
        @media (max-width: 1900px) {
            height: 600px;
        }
        @media (max-width: 1600px) {
            height: 350px;
        }
        .image {
            background: var(--white-100);
            width: 100%;
            height: 100%;
            position: relative;
            overflow: hidden;
            @media @max-mobile {
                pointer-events: none;
            }

            .image-content {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                transition: transform 0.1s ease-out;
                img {
                    height: auto;
                    width: auto;
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    position: relative;
                }
            }
            &:hover,
            &:hover .lasersheet-container {
                cursor: zoom-in;
            }
            &.allowZoom:hover,
            &.allowZoom:hover .lasersheet-container {
                cursor: zoom-out;
            }
            &.allowZoom:hover .image-content {
                transform: scale(2.2);
            }
        }

        .lasersheet-button {
            position: absolute;
            bottom: 1.2rem;
            right: 1.2rem;
            width: 2.4rem;
            height: 2.4rem;
            display: flex;
            justify-content: center;
            align-items: center;
            border-radius: 100px;
            background-color: var(--tertiary);
            color: white;
            & > * {
                transform: translate(10%, 10%);
            }
            display: none;
        }
        @media @max-mobile-product {
            .lasersheet-button {
                display: unset;
            }
        }

        .note-badge {
            position: absolute;
            bottom: 1.2rem;
            right: 1.2rem;
            z-index: 4;
            background: none;
            border: none;
            padding: 0;
            cursor: default;

            .note-label {
                display: inline-flex;
                align-items: center;
                gap: 0.3rem;
                padding: 0.5rem 1rem;
                background-color: rgba(100, 100, 100, 0.7);
                color: var(--white-100, #fff);
                font-size: 0.9rem;
                font-weight: 600;
                border-radius: 4px;
                white-space: nowrap;
                backdrop-filter: blur(4px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
                transition: all 0.25s ease;
            }

            &:hover .note-label {
                background-color: rgba(80, 80, 80, 0.85);
                box-shadow:
                    0 4px 16px rgba(0, 0, 0, 0.3),
                    0 1px 4px rgba(0, 0, 0, 0.15);
                transform: translateY(-1px);
            }

            .note-tooltip {
                display: none;
                position: absolute;
                bottom: calc(100% + 0.75rem);
                right: 0;
                min-width: 220px;
                max-width: 320px;
                padding: 0.85rem 1.1rem;
                background-color: var(--white-100, #fff);
                color: var(--on-surface, #333);
                font-size: 0.85rem;
                font-weight: 400;
                line-height: 1.45;
                border-radius: 6px;
                box-shadow:
                    0 8px 32px rgba(0, 0, 0, 0.2),
                    0 2px 8px rgba(0, 0, 0, 0.1),
                    0 0 0 1px rgba(0, 0, 0, 0.05);
                white-space: normal;
            }

            &:hover .note-tooltip,
            &:focus .note-tooltip,
            &:focus-within .note-tooltip {
                display: block;
            }
        }
    }
</style>
